import React, { useEffect, useRef, useState } from 'react';
import { useTranslations } from 'use-intl';
import { toTranslationKey } from '@/utils/translations';
import { Clock, PlayCircle, PauseCircle, AlertCircle, Send, CheckCircle, Eye } from 'react-feather';
import { Typography } from '@/UIlibrary/Typography/Typography';
import {
	ChatMessage,
	InteractiveMessageTypes,
	MessageStatus,
	MessageStatusFailureMetadata,
	SenderTypes,
	TemplateStatus
} from './types';
import { useSupabaseStore } from './supabaseStore';
import FormContent from './FormContent';
import ImageContent from './ImageContent';
import DocumentContent from './DocumentContent';
import { TextBadge } from '@/UIlibrary/TextBadge/TextBadge';
import Button from '@/UIlibrary/Button/Button';
import Modal from '@/UIlibrary/Modal/Modal';

interface MessageListProps {
	messages: ChatMessage[];
	currentUser?: {
		id: string;
		name: string;
		avatar?: string;
	};
	isLoadingMessages: boolean;
}

// Voice Content Component
const VoiceContent: React.FC<{ url: string }> = ({ url }) => {
	const [error, setError] = useState<string | null>(null);
	const [signedUrl, setSignedUrl] = useState<string | null>(null);
	const [isPlaying, setIsPlaying] = useState(false);
	const [duration, setDuration] = useState(0);
	const [currentTime, setCurrentTime] = useState(0);
	const audioRef = useRef<HTMLAudioElement | null>(null);
	const progressIntervalRef = useRef<number | null>(null);
	const supabase = useSupabaseStore((state) => state.supabase);

	useEffect(() => {
		const getSignedUrl = async () => {
			try {
				const pathMatch = url.match(/whatsapp-voice-notes\/(.*)/);
				if (!pathMatch) {
					throw new Error('Invalid storage URL format');
				}

				const path = pathMatch[1];
				const { data, error } = await supabase.storage
					.from('whatsapp-voice-notes')
					.createSignedUrl(path, 3600);

				if (error) throw error;
				setSignedUrl(data.signedUrl);
			} catch (err) {
				console.error('Error getting signed URL:', err);
				setError('Failed to get voice note access');
			}
		};

		if (url) {
			getSignedUrl();
		}

		return () => {
			if (progressIntervalRef.current) {
				clearInterval(progressIntervalRef.current);
			}
			if (audioRef.current) {
				audioRef.current.pause();
			}
		};
	}, [url]);

	const formatTime = (time: number) => {
		const minutes = Math.floor(time / 60);
		const seconds = Math.floor(time % 60);
		return `${minutes}:${seconds.toString().padStart(2, '0')}`;
	};

	const startProgressUpdate = () => {
		progressIntervalRef.current = window.setInterval(() => {
			if (audioRef.current) {
				setCurrentTime(audioRef.current.currentTime);
			}
		}, 100);
	};

	const stopProgressUpdate = () => {
		if (progressIntervalRef.current) {
			clearInterval(progressIntervalRef.current);
		}
	};

	const togglePlayback = () => {
		if (!audioRef.current || !signedUrl) return;

		if (isPlaying) {
			audioRef.current.pause();
			stopProgressUpdate();
		} else {
			audioRef.current.play();
			startProgressUpdate();
		}
		setIsPlaying(!isPlaying);
	};

	const handleAudioEnded = () => {
		setIsPlaying(false);
		stopProgressUpdate();
		if (audioRef.current) {
			audioRef.current.currentTime = 0;
			setCurrentTime(0);
		}
	};

	return (
		<div className="flex items-center space-x-2 bg-gray-50 rounded-lg p-3 min-w-[200px]">
			{signedUrl && (
				<audio
					ref={audioRef}
					src={signedUrl}
					onEnded={handleAudioEnded}
					onLoadedMetadata={() => {
						if (audioRef.current) {
							setDuration(audioRef.current.duration);
						}
					}}
					onError={() => setError('Failed to load audio')}
				/>
			)}

			<button
				onClick={togglePlayback}
				disabled={!signedUrl || !!error}
				className="focus:outline-none disabled:opacity-50"
			>
				{isPlaying ? (
					<PauseCircle className="w-10 h-10 text-blue-500" />
				) : (
					<PlayCircle className="w-10 h-10 text-blue-500" />
				)}
			</button>

			<div className="flex-1">
				<div className="w-full bg-gray-200 rounded-full h-2 mb-1">
					<div
						className="bg-blue-500 h-2 rounded-full transition-all duration-100"
						style={{
							width: `${(currentTime / duration) * 100}%`
						}}
					/>
				</div>
				<div className="flex justify-between text-xs text-gray-500">
					<span>{formatTime(currentTime)}</span>
				</div>
			</div>

			{error && <div className="text-red-500 text-sm ml-2">{error}</div>}
		</div>
	);
};

const formatTimestamp = (timestamp: string) => {
	const date = new Date(timestamp);
	const day = date.getDate().toString().padStart(2, '0');
	const month = (date.getMonth() + 1).toString().padStart(2, '0');
	const year = date.getFullYear();
	const hours = date.getHours().toString().padStart(2, '0');
	const minutes = date.getMinutes().toString().padStart(2, '0');
	return `${day}/${month}/${year} ${hours}:${minutes}`;
};

const MessageList: React.FC<MessageListProps> = ({ messages, isLoadingMessages }) => {
	const t = useTranslations('Support');
	const messagesEndRef = useRef<HTMLDivElement | null>(null);
	const [highlightedId, setHighlightedId] = useState<string | null>(null);
	const [selectedMessageError, setSelectedMessageError] =
		useState<MessageStatusFailureMetadata | null>(null);
	const [isMessageErrorModalOpen, setIsMessageErrorModalOpen] = useState(false);

	useEffect(() => {
		messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
		if (messages.length > 0) {
			const lastMessage = messages[messages.length - 1];
			setHighlightedId(lastMessage.id.toString());
			setTimeout(() => setHighlightedId(null), 2000);
		}
	}, [messages]);

	const handleSetMessageError = (errorMessage: MessageStatusFailureMetadata) => {
		if (!errorMessage) return;
		setSelectedMessageError(errorMessage);
		setIsMessageErrorModalOpen(true);
	};

	const getMessageStatusElement = (message: ChatMessage) => {
		switch (message.status) {
			case MessageStatus.SENT:
				return <TextBadge text={t('Sent')} type="primary" size="xs" icon={<Send size={14} />} />;
			case MessageStatus.DELIVERED:
				return (
					<TextBadge
						text={t('Received')}
						type="success"
						size="xs"
						icon={<CheckCircle size={14} />}
					/>
				);
			case MessageStatus.READ:
				return <TextBadge text={t('Read')} type="success" size="xs" icon={<Eye size={14} />} />;
			case MessageStatus.DELIVERY_FAILED:
				return (
					<div className="flex items-center gap-2">
						<TextBadge
							text={t('NotSent')}
							type="error"
							size="xs"
							icon={<AlertCircle size={14} />}
						/>
						<Button
							label="Conoce más"
							textOnly
							size="small"
							onClick={() => handleSetMessageError(message?.metadata?.failure || {})}
						/>
					</div>
				);
			default:
				return <TextBadge text={t('UnknownStatus')} type="gray" size="xs" />;
		}
	};

	if (isLoadingMessages) {
		return (
			<div className="flex items-center justify-center p-4">
				<Typography color="subtitle">{t('LoadingMessages')}</Typography>
			</div>
		);
	}

	if (messages.length === 0) {
		return (
			<div className="flex items-center justify-center p-4 min-h-[60vh]">
				<Typography size="xl" color="subtitle">
					{t('NoMessagesYet')}
				</Typography>
			</div>
		);
	}

	const SystemMessage = ({ message }: { message: ChatMessage }) => {
		const templateStatus = message.metadata?.status;

		switch (templateStatus) {
			case TemplateStatus.PENDING:
				return <TextBadge text={message.content} type="primary" size="xs" />;
			case TemplateStatus.APPROVED:
				return <TextBadge text={message.content} type="success" size="xs" />;
			case TemplateStatus.PAUSED:
			case TemplateStatus.PENDING_DELETION:
				return <TextBadge text={message.content} type="warning" size="xs" />;
			case TemplateStatus.REJECTED:
			case TemplateStatus.FLAGGED:
				return <TextBadge text={message.content} type="error" size="xs" />;
			default:
				return <TextBadge text={message.content} type="gray" size="xs" />;
		}
	};

	return (
		<div className="flex flex-col space-y-4 p-4">
			{messages.map((message) => {
				const isHighlighted = highlightedId === message.id.toString();
				const isFromCustomer = message.is_from_customer;
				const isSystemMessage = message.sender_type === SenderTypes.SYSTEM;
				return isSystemMessage ? (
					<div className="flex justify-center">
						<SystemMessage key={message.id} message={message} />
					</div>
				) : (
					<div
						key={message.id}
						className={`flex ${isFromCustomer ? 'justify-start' : 'justify-end'}`}
					>
						<div
							className={`max-w-[70%] flex flex-col ${isFromCustomer ? 'items-start' : 'items-end'}`}
						>
							{/* Message Type Indicator */}
							<div
								className={`flex items-center gap-2 mb-1 
                ${isFromCustomer ? 'flex-row' : 'flex-row-reverse'}`}
							>
								<Typography size="xs" color="subtitle">
									{message?.message_type && t(toTranslationKey(message.message_type.toLowerCase()))}
								</Typography>
								<Typography size="xs" color="subtitle">
									{t(toTranslationKey(message.sender_type || 'unknown'))}
								</Typography>
							</div>

							{message.media_url && message.message_type === 'image' && (
								<div className="mb-2">
									<ImageContent url={message.media_url} />
								</div>
							)}

							{message.media_url && message.message_type === 'document' && (
								<div className="mb-2">
									<DocumentContent url={message.media_url} />
								</div>
							)}

							{message.voice_url && message.message_type === 'voice' && (
								<div className="mb-2">
									<VoiceContent url={message.voice_url} />
								</div>
							)}

							{message.content_json &&
								message.content_json.type === InteractiveMessageTypes.FLOW && (
									<div className="mb-2">
										<FormContent data={message.content_json} />
									</div>
								)}

							{/* Message Content */}
							{message.content && (
								<div className="flex items-center gap-2">
									<div
										className={`inline-block rounded-lg transition-all duration-500 shadow-sm
											${isFromCustomer ? 'bg-gray-100 rounded-bl-none' : 'bg-blue-100 rounded-br-none'}
											${isHighlighted ? 'ring-2 ring-yellow-300' : ''}`}
									>
										<div className="p-3">
											<Typography size="sm">{message.content}</Typography>
										</div>
										{/* Message Metadata */}
										<div
											className={`flex items-center gap-2 mt-1 p-1 text-xs text-gray-500 ${isFromCustomer ? 'flex-row' : 'flex-row-reverse'}`}
										>
											<div className="flex items-center gap-1">
												<Clock className="w-3 h-3" />
												<span>{formatTimestamp(message.sent_at)}</span>
											</div>
										</div>
									</div>
									<span
										className={`${!isFromCustomer && message.status === 'delivery_failed' ? 'visible' : 'hidden'}`}
									>
										<AlertCircle color="red" size="24" />
									</span>
								</div>
							)}

							{/* Message status */}
							<div
								className={`flex items-center gap-2 mt-1 text-xs text-gray-500 ${isFromCustomer ? 'flex-row' : 'flex-row-reverse'}`}
							>
								<div className="flex items-center gap-1">
									{!isFromCustomer && <span>{getMessageStatusElement(message)}</span>}
								</div>
							</div>
						</div>
					</div>
				);
			})}
			<div ref={messagesEndRef} />
			<Modal
				isOpen={isMessageErrorModalOpen}
				onClose={() => setIsMessageErrorModalOpen(false)}
				closeable
			>
				<div className="flex flex-col justify-center gap-4">
					<div className="border-b-2 w-full mb-5">
						<Typography size="lg">{t('MessageFailedToDeliver')}</Typography>
					</div>
					<div className="flex flex-col gap-4">
						<Typography color="subtitle" size="sm">
							{selectedMessageError?.friendly_message}
						</Typography>
						<div className="flex flex-col gap-1 mt-4">
							<Typography size="sm">
								{t('ErrorCode')} : {selectedMessageError?.error_code}
							</Typography>
							<a
								className="text-blue-600 visited:text-violet-600 underline text-sm"
								href="https://developers.facebook.com/docs/whatsapp/cloud-api/support/error-codes/?locale=es_LA"
								about="_blank"
							>
								Conoce más acerca de los errores de WhatsApp
							</a>
						</div>
					</div>
				</div>
			</Modal>
		</div>
	);
};

export default MessageList;
